import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  ChevronDown,
  ChevronUp,
  Server,
  FlaskConical,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Check if we're in a dev environment (not production)
function isDevEnvironment() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname.includes("preview") ||
    hostname.includes("127.0.0.1") ||
    process.env.NODE_ENV === "development"
  );
}

// Smoke test payloads as specified
const SMOKE_TESTS = [
  {
    name: "TEST 1: Ambiguous Expression",
    description: "Should return streak_can_change=false, answer_correct=null",
    expectedChecks: [
      { field: "streak_can_change", expected: false },
      { field: "answer_correct", expected: null },
      { field: "invariant_reason", contains: ["ambiguous_expression", "math_safe_mode_restriction"] },
    ],
    payload: {
      question_text: "Evaluate 3a^2 when a=4",
      correct_answer: "48",
      student_answer: "48",
      subject: "mathematics",
      options: ["12", "24", "48", "64"],
      correct_option_index: 2,
      needs_streak_safe: false,
      streak_reset_allowed: true,
      is_gradable: true,
    },
  },
  {
    name: "TEST 2: Unambiguous Expression",
    description: "Should return streak_can_change=true, answer_correct=true",
    expectedChecks: [
      { field: "streak_can_change", expected: true },
      { field: "answer_correct", expected: true },
    ],
    payload: {
      question_text: "Evaluate 3*(a^2) when a=4",
      correct_answer: "48",
      student_answer: "48",
      subject: "mathematics",
      options: ["12", "24", "48", "64"],
      correct_option_index: 2,
      needs_streak_safe: false,
      streak_reset_allowed: true,
      is_gradable: true,
    },
  },
  {
    name: "TEST 3: Correct Option Mismatch",
    description: "Should return streak_can_change=false, invariant_reason=correct_option_mismatch OR symbolic_mismatch",
    expectedChecks: [
      { field: "streak_can_change", expected: false },
      { field: "answer_correct", expected: null },
      { field: "invariant_reason", contains: ["correct_option_mismatch", "symbolic_mismatch"] },
      { field: "expected_index", exists: true },
    ],
    payload: {
      question_text: "Evaluate (6^3)*x + y*z when x=3, y=2, z=0.5",
      correct_answer: "649",
      student_answer: "649",
      subject: "mathematics",
      options: ["648", "649", "650", "651"],
      correct_option_index: 0,
      needs_streak_safe: false,
      streak_reset_allowed: true,
      is_gradable: true,
    },
  },
];

// Validate a single test result against expected checks
function validateTestResult(result, expectedChecks) {
  const failures = [];

  for (const check of expectedChecks) {
    const { field, expected, contains, exists } = check;
    const actual = result[field];

    if (exists !== undefined) {
      // Check if field exists
      if (exists && actual === undefined) {
        failures.push(`${field} should exist but is undefined`);
      } else if (!exists && actual !== undefined) {
        failures.push(`${field} should not exist but is ${JSON.stringify(actual)}`);
      }
    } else if (contains !== undefined) {
      // Check if field contains any of the expected values
      const found = contains.some((val) =>
        typeof actual === "string" ? actual.includes(val) : actual === val
      );
      if (!found) {
        failures.push(`${field}=${JSON.stringify(actual)} should contain one of [${contains.join(", ")}]`);
      }
    } else {
      // Direct equality check
      if (actual !== expected) {
        failures.push(`${field}=${JSON.stringify(actual)} (expected ${JSON.stringify(expected)})`);
      }
    }
  }

  return failures;
}

export default function DevMathTestPanel() {
  const [isVisible, setIsVisible] = useState(false);
  const [healthInfo, setHealthInfo] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [expandedTests, setExpandedTests] = useState({});

  // Check if we're in dev environment
  const isDev = isDevEnvironment();

  // Fetch health info when panel becomes visible
  useEffect(() => {
    if (isDev && isVisible && !healthInfo) {
      fetchHealthInfo();
    }
  }, [isDev, isVisible, healthInfo]);

  const fetchHealthInfo = async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      const response = await axios.get(`${API}/health`);
      setHealthInfo(response.data);
    } catch (err) {
      setHealthError(err.message || "Failed to fetch health info");
    } finally {
      setHealthLoading(false);
    }
  };

  // Don't render in production
  if (!isDev) {
    return null;
  }

  const runSmokeTests = async () => {
    setIsRunning(true);
    setTestResults([]);
    const results = [];

    for (let i = 0; i < SMOKE_TESTS.length; i++) {
      const test = SMOKE_TESTS[i];
      const testResult = {
        name: test.name,
        description: test.description,
        status: "running",
        response: null,
        failures: [],
        error: null,
      };

      try {
        const response = await axios.post(`${API}/math/check-answer`, test.payload);
        testResult.response = response.data;
        testResult.failures = validateTestResult(response.data, test.expectedChecks);
        testResult.status = testResult.failures.length === 0 ? "pass" : "fail";
      } catch (err) {
        testResult.status = "error";
        testResult.error = err.response?.data?.detail || err.message || "Request failed";
      }

      results.push(testResult);
      setTestResults([...results]);
    }

    setIsRunning(false);
  };

  const toggleTestExpanded = (index) => {
    setExpandedTests((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const passCount = testResults.filter((t) => t.status === "pass").length;
  const totalTests = SMOKE_TESTS.length;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="fixed bottom-4 left-4 z-50 bg-slate-900 text-white p-3 rounded-full shadow-lg hover:bg-slate-800 transition-colors"
        data-testid="dev-panel-toggle"
        title="Toggle Dev Math Test Panel"
      >
        <FlaskConical className="w-5 h-5" />
      </button>

      {/* Panel */}
      {isVisible && (
        <div
          className="fixed bottom-20 left-4 z-50 w-[420px] max-h-[80vh] overflow-hidden bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-700"
          data-testid="dev-math-test-panel"
        >
          <div className="p-4 border-b border-slate-700 bg-slate-800">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-amber-400 flex items-center gap-2">
                <FlaskConical className="w-4 h-4" />
                DEV Math Verification Panel
              </h3>
              <button
                onClick={() => setIsVisible(false)}
                className="text-slate-400 hover:text-white"
              >
                ×
              </button>
            </div>
          </div>

          <div className="p-4 overflow-y-auto max-h-[calc(80vh-80px)]">
            {/* Backend Info Section */}
            <div className="mb-4">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                <Server className="w-3 h-3" />
                Backend Connection
              </h4>
              <div className="bg-slate-800 rounded-lg p-3 text-xs font-mono space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-400">Base URL:</span>
                  <span className="text-cyan-400 truncate ml-2" data-testid="backend-url">
                    {process.env.REACT_APP_BACKEND_URL}
                  </span>
                </div>
                {healthLoading && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading health info...
                  </div>
                )}
                {healthError && (
                  <div className="text-red-400">Error: {healthError}</div>
                )}
                {healthInfo && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Status:</span>
                      <span className="text-green-400" data-testid="health-status">
                        {healthInfo.status}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Commit:</span>
                      <span className="text-amber-400" data-testid="health-commit">
                        {healthInfo.git_commit}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Build:</span>
                      <span className="text-slate-300" data-testid="health-build">
                        {healthInfo.build_time_utc}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Env:</span>
                      <span className="text-purple-400" data-testid="health-env">
                        {healthInfo.env_name}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Smoke Tests Section */}
            <div>
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                Math Invariant Smoke Tests
              </h4>

              <Button
                onClick={runSmokeTests}
                disabled={isRunning}
                className="w-full mb-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="run-smoke-tests-btn"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running Tests...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Run Math Smoke Tests
                  </>
                )}
              </Button>

              {/* Summary */}
              {testResults.length > 0 && (
                <div
                  className={`mb-3 p-2 rounded-lg text-center font-semibold ${
                    passCount === totalTests
                      ? "bg-green-900/50 text-green-400"
                      : "bg-red-900/50 text-red-400"
                  }`}
                  data-testid="smoke-test-summary"
                >
                  Smoke Tests: {passCount}/{totalTests} PASS
                </div>
              )}

              {/* Test Results */}
              <div className="space-y-2">
                {testResults.map((result, index) => (
                  <div
                    key={index}
                    className={`rounded-lg border ${
                      result.status === "pass"
                        ? "border-green-700 bg-green-900/20"
                        : result.status === "fail"
                        ? "border-red-700 bg-red-900/20"
                        : result.status === "error"
                        ? "border-orange-700 bg-orange-900/20"
                        : "border-slate-600 bg-slate-800"
                    }`}
                    data-testid={`test-result-${index}`}
                  >
                    <button
                      onClick={() => toggleTestExpanded(index)}
                      className="w-full p-3 flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2">
                        {result.status === "pass" && (
                          <CheckCircle className="w-4 h-4 text-green-400" />
                        )}
                        {result.status === "fail" && (
                          <XCircle className="w-4 h-4 text-red-400" />
                        )}
                        {result.status === "error" && (
                          <XCircle className="w-4 h-4 text-orange-400" />
                        )}
                        {result.status === "running" && (
                          <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                        )}
                        <span className="text-sm font-medium">{result.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-xs px-2 py-0.5 rounded ${
                            result.status === "pass"
                              ? "bg-green-800 text-green-300"
                              : result.status === "fail"
                              ? "bg-red-800 text-red-300"
                              : result.status === "error"
                              ? "bg-orange-800 text-orange-300"
                              : "bg-slate-700 text-slate-300"
                          }`}
                          data-testid={`test-status-${index}`}
                        >
                          {result.status.toUpperCase()}
                        </span>
                        {expandedTests[index] ? (
                          <ChevronUp className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {expandedTests[index] && (
                      <div className="px-3 pb-3 text-xs">
                        <p className="text-slate-400 mb-2">{result.description}</p>
                        
                        {result.failures.length > 0 && (
                          <div className="mb-2 p-2 bg-red-900/30 rounded text-red-300">
                            <strong>Failures:</strong>
                            <ul className="list-disc list-inside mt-1">
                              {result.failures.map((f, i) => (
                                <li key={i}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {result.error && (
                          <div className="mb-2 p-2 bg-orange-900/30 rounded text-orange-300">
                            <strong>Error:</strong> {result.error}
                          </div>
                        )}

                        {result.response && (
                          <div className="p-2 bg-slate-800 rounded overflow-x-auto">
                            <strong className="text-slate-400">Response:</strong>
                            <pre className="mt-1 text-slate-300 whitespace-pre-wrap break-all">
                              {JSON.stringify(result.response, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
